import { PRIVATEPATHS } from '@core/enums/paths.enum';
import { IBatch } from '@core/models/batch.model';
import { getBatchesThunk } from '@redux/features/batch.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddBatchModal from './AddBatchModal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import {
    formatBatchDate,
    formatBatchDateOnly,
    productLineCount,
    totalBatchQty,
    totalSerialCount,
} from './batch-detail.utils';

const Batch: FC = () => {
    const [page, setPage] = useState<number>(1);
    const [showAddBatchModal, setShowAddBatchModal] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { batches, loading } = useAppSelector((state: RootState) => state.batchState);

    const changePage = (p: string) => {
        setPage(Number(p));
    };

    useEffect(() => {
        dispatch(getBatchesThunk({ pageSize: 10, pageNumber: page - 1 }));
    }, [page, dispatch]);

    const handleCloseAddBatchModal = () => {
        setShowAddBatchModal(false);
        dispatch(getBatchesThunk({ pageSize: 10, pageNumber: page - 1 }));
    };

    const openDetail = (batch: IBatch) => {
        if (batch.id != null) {
            navigate(`${PRIVATEPATHS.Batch}/${batch.id}`);
        }
    };

    return (
        <>
            <AddBatchModal show={showAddBatchModal} handleClose={handleCloseAddBatchModal} />
            <BreadCrumb pageTitle="Batch List" />
            <section className="py-5">
                <div className="flex flex-col gap-2 bg-[#fff] p-3">
                    <div className="flex w-full flex-wrap items-center justify-between gap-2">
                        <div>
                            <h3 className="font-extrabold text-[#bd902e]">Batch List</h3>
                            <p className="text-xs text-slate-500">
                                Newest batches first. Click a row to expand all fields.
                            </p>
                        </div>
                        <ButtonBase innerText="Add Batch" onClick={() => setShowAddBatchModal(true)} />
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <div>
                            <div className="overflow-x-auto">
                                <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                    <thead className="ltr:text-left rtl:text-right">
                                        <tr className="relative rounded-md bg-slate-50">
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase">ID</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase">Created</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase" title="GS1 desadv from EPCIS shipping">
                                                Shipment ID
                                            </th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase">Expiry</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase">PO</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase">Invoice</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase">
                                                Packaging
                                            </th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase">
                                                Warehouse
                                            </th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase">
                                                Supplier
                                            </th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase">
                                                Products
                                            </th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase">
                                                Total qty
                                            </th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase">
                                                Serials
                                            </th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <AnimatedTableBody
                                        loading={loading}
                                        itemCount={batches?.content?.length ?? 0}
                                        animationKey={`batch-${page}`}
                                    >
                                        {batches?.content?.map((batch: IBatch) => (
                                            <AnimatedTableRow
                                                key={batch?.id}
                                                rowKey={batch?.id}
                                                detailRecord={batch}
                                                expandTitle={`Batch #${batch?.id ?? ''} — all data`}
                                                className="relative rounded-md bg-slate-50 hover:bg-[#bd902e10]"
                                            >
                                                <td className="px-3.5 py-2.5">{batch?.id ?? '—'}</td>
                                                <td className="px-3.5 py-2.5 text-xs text-slate-600">
                                                    {formatBatchDate(batch?.createdAt)}
                                                </td>
                                                <td className="px-3.5 py-2.5 font-medium">{batch?.lotNumber || '—'}</td>
                                                <td className="px-3.5 py-2.5 text-xs">
                                                    {formatBatchDateOnly(batch?.expiryDate)}
                                                </td>
                                                <td className="px-3.5 py-2.5">{batch?.purchaseOrderNumber || '—'}</td>
                                                <td className="px-3.5 py-2.5">{batch?.invoiceNumber || '—'}</td>
                                                <td className="px-3.5 py-2.5">{batch?.packagingInfo || '—'}</td>
                                                <td className="px-3.5 py-2.5">{batch?.warehouseName || '—'}</td>
                                                <td className="max-w-[140px] truncate px-3.5 py-2.5 text-xs">
                                                    {batch?.preferredSupplierBusinessName ||
                                                        batch?.preferredSupplierEmail ||
                                                        '—'}
                                                </td>
                                                <td className="px-3.5 py-2.5 text-center">
                                                    {productLineCount(batch)}
                                                </td>
                                                <td className="px-3.5 py-2.5 text-center font-semibold">
                                                    {totalBatchQty(batch)}
                                                </td>
                                                <td className="px-3.5 py-2.5 text-center">{totalSerialCount(batch)}</td>
                                                <td className="px-3.5 py-2.5">
                                                    <button
                                                        type="button"
                                                        className="no-row-toggle mx-auto flex w-full items-center justify-center rounded-md border border-yellow-600 bg-white px-2 py-1 text-xs font-semibold transition-all hover:scale-105 hover:bg-yellow-50"
                                                        onClick={() => openDetail(batch)}
                                                    >
                                                        View full detail
                                                    </button>
                                                </td>
                                            </AnimatedTableRow>
                                        ))}
                                    </AnimatedTableBody>
                                </AnimatedTable>
                            </div>
                            <Pagination
                                perPageData={batches?.pageable?.pageSize || 10}
                                total={batches?.totalElements || 0}
                                currentPage={page}
                                changePage={changePage}
                                length={batches?.numberOfElements || batches?.content?.length || 0}
                            />
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};

export default Batch;
